/**
 * Comprehensive text cleaning utilities for PDF-extracted content
 */

// Enhanced logging for debugging
function debugLog(message: string, data?: any) {
  console.log(`🧹 TextCleaner: ${message}`, data ? JSON.stringify(data, null, 2) : '');
}

/**
 * Remove PDF metadata and artifacts (BALANCED APPROACH)
 * Only removes clear PDF technical artifacts, preserves content
 */
function removePDFMetadata(text: string): string {
  if (!text) return '';

  let cleaned = text;

  // Remove repeated "Adobe Identity" patterns (common PDF metadata)
  cleaned = cleaned.replace(/(Adobe\s+Identity\s+){2,}/gi, '');
  cleaned = cleaned.replace(/Adobe\s+Identity/gi, '');
  cleaned = cleaned.replace(/Adobe\s+User/gi, '');

  // Remove Microsoft Word metadata
  cleaned = cleaned.replace(/User\s+Microsoft\s+Word/gi, '');
  cleaned = cleaned.replace(/Microsoft\s+Word\s+D:\+00'00'/gi, '');
  cleaned = cleaned.replace(/D:\+00'00'/g, '');

  // Remove PDF structure elements (these are never legitimate content)
  cleaned = cleaned.replace(/\b\d+\s+0\s+obj\b/g, '');
  cleaned = cleaned.replace(/\bendobj\b/g, '');
  cleaned = cleaned.replace(/\bstream\b[\s\S]*?\bendstream\b/g, '');
  cleaned = cleaned.replace(/\bxref\b[\s\S]*?\bstartxref\b/g, '');
  cleaned = cleaned.replace(/\btrailer\b[\s\S]*?%%EOF/g, '');

  // Remove font and encoding metadata (aggressive)
  cleaned = cleaned.replace(/Win\s+Ansi\s+Encoding/gi, '');
  cleaned = cleaned.replace(/Font\s+Descriptor/gi, '');
  cleaned = cleaned.replace(/Italic\s+Angle/gi, '');
  cleaned = cleaned.replace(/Font\s+Weight/gi, '');
  cleaned = cleaned.replace(/Descendant\s+Fonts/gi, '');
  cleaned = cleaned.replace(/CIDFont\s+Type/gi, '');
  cleaned = cleaned.replace(/CIDTo\s+GIDMap/gi, '');
  cleaned = cleaned.replace(/CIDSystem\s+Info/gi, '');
  cleaned = cleaned.replace(/Struct\s+Tree\s+Root/gi, '');
  cleaned = cleaned.replace(/Struct\s+Parents/gi, '');
  cleaned = cleaned.replace(/Marked\s+true/gi, '');
  cleaned = cleaned.replace(/Viewer\s+Preferences/gi, '');
  cleaned = cleaned.replace(/Flate\s+Decode/gi, '');
  cleaned = cleaned.replace(/Transparency/gi, '');
  cleaned = cleaned.replace(/Supplement/gi, '');

  // Remove specific font names that appear as metadata
  cleaned = cleaned.replace(/Times\s+New\s+Roman\s+(PS|PSMT|PS\s+Bold\s+Italic\s+MT)/gi, '');
  cleaned = cleaned.replace(/Book\s+Antiqua/gi, '');

  // Remove PDF commands ONLY when isolated (not part of actual text)
  cleaned = cleaned.replace(/\s+(BT|ET|Tf|Td|Tj|TJ|Tm)\s+/g, ' ');

  // Remove ONLY pure coordinate data (6+ numbers in sequence)
  cleaned = cleaned.replace(/\b\d+\.\d+\s+\d+\.\d+\s+\d+\.\d+\s+\d+\.\d+\s+\d+\.\d+\s+\d+\.\d+\b/g, '');

  // Remove ONLY PDF font commands (not affecting actual text)
  cleaned = cleaned.replace(/\/F\d+\s+\d+\.?\d*\s+Tf/g, '');

  // Remove ONLY pure hex strings (8+ hex chars, which are unlikely to be real content)
  cleaned = cleaned.replace(/\b[0-9A-Fa-f]{8,}\b/g, '');

  // Remove ONLY repeated patterns (3+ repetitions indicates a glitch)
  cleaned = cleaned.replace(/(\b\w+\b)(\s+\1){2,}/gi, '$1');

  // Remove creation date patterns
  cleaned = cleaned.replace(/Creation\s+Date/gi, '');
  cleaned = cleaned.replace(/Creator\s+Tool/gi, '');

  return cleaned;
}

/**
 * Extract meaningful text patterns from PDF content (CONSERVATIVE APPROACH)
 * Only filters out obvious junk, preserves most content
 */
function extractMeaningfulText(text: string): string {
  if (!text) return '';

  // Split into lines and process each line
  const lines = text.split('\n');
  const meaningfulLines: string[] = [];

  for (const line of lines) {
    const cleanLine = line.trim();

    // Skip only truly empty lines
    if (!cleanLine || cleanLine.length === 0) continue;

    // Skip ONLY lines that are purely technical PDF artifacts
    if (cleanLine.match(/^[0-9\s<>/\\{}[\]()]+$/)) continue;
    if (cleanLine.match(/^(obj|endobj|stream|endstream|xref|startxref|trailer)$/i)) continue;

    // Skip ONLY lines with extreme special character density (>80%)
    const specialCharRatio = (cleanLine.match(/[^a-zA-Z0-9\s@.,-]/g) || []).length / cleanLine.length;
    if (specialCharRatio > 0.8) continue;

    // Skip ONLY pure PDF metadata markers (exact matches)
    const pdfMetadataMarkers = /^(Font\s+Descriptor|CIDFont\s+Type|Win\s+Ansi\s+Encoding|Struct\s+Tree\s+Root|Flate\s+Decode)$/i;
    if (pdfMetadataMarkers.test(cleanLine)) continue;

    // Keep everything else - be conservative!
    // Even single letters or numbers might be section headers or bullet points
    meaningfulLines.push(cleanLine);
  }

  return meaningfulLines.join('\n');
}

/**
 * Clean and format professional document text
 */
function formatProfessionalText(text: string): string {
  if (!text) return '';
  
  return text
    // Fix spacing around punctuation
    .replace(/\s+([,.!?;:])/g, '$1')
    .replace(/([.!?])\s*([A-Z])/g, '$1 $2')
    
    // Fix common formatting issues
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add spaces between camelCase
    .replace(/(\d)([A-Z])/g, '$1 $2') // Add spaces between numbers and letters
    
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    .replace(/\n\s*\n\s*\n/g, '\n\n') // Normalize paragraph breaks
    
    // Fix common professional terms
    .replace(/\bLinked In\b/gi, 'LinkedIn')
    .replace(/\bFace Book\b/gi, 'Facebook')
    .replace(/\bMicro Soft\b/gi, 'Microsoft')
    .replace(/\bGoo gle\b/gi, 'Google')
    
    // Remove excessive repetition
    .replace(/(\b\w+\b)(\s+\1){2,}/gi, '$1')
    
    .trim();
}

/**
 * Detect and extract contact information
 */
function extractContactInfo(text: string): {
  emails: string[];
  phones: string[];
  locations: string[];
} {
  const emails = text.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g) || [];
  const phones = text.match(/\b(?:\+?1[-.\s]?)?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}\b/g) || [];
  const locations = text.match(/\b[A-Z][a-z]+,\s*[A-Z]{2}\b/g) || [];
  
  return {
    emails: [...new Set(emails)],
    phones: [...new Set(phones)],
    locations: [...new Set(locations)]
  };
}

/**
 * Main text cleaning function for PDF-extracted content (ENHANCED)
 */
export function cleanExtractedText(rawText: string): {
  cleanedText: string;
  originalLength: number;
  cleanedLength: number;
  contactInfo: {
    emails: string[];
    phones: string[];
    locations: string[];
  };
  metadata: {
    hadPDFArtifacts: boolean;
    hadAdobeMetadata: boolean;
    hadMicrosoftMetadata: boolean;
    compressionRatio: number;
  };
} {
  debugLog(`🚀 Starting ENHANCED text cleaning`, {
    originalLength: rawText.length,
    preview: rawText.substring(0, 200) + '...'
  });

  const originalLength = rawText.length;
  
  // Step 1: Remove PDF metadata and artifacts (ENHANCED)
  let cleanedText = removePDFMetadata(rawText);
  debugLog('📋 After enhanced PDF metadata removal', {
    length: cleanedText.length,
    preview: cleanedText.substring(0, 200) + '...'
  });
  
  // Step 2: Extract meaningful text patterns
  cleanedText = extractMeaningfulText(cleanedText);
  debugLog('🎯 After meaningful text extraction', {
    length: cleanedText.length,
    preview: cleanedText.substring(0, 200) + '...'
  });
  
  // Step 3: Format for professional documents
  cleanedText = formatProfessionalText(cleanedText);
  debugLog('✨ After professional formatting', {
    length: cleanedText.length,
    preview: cleanedText.substring(0, 200) + '...'
  });
  
  // Step 4: Extract contact information
  const contactInfo = extractContactInfo(cleanedText);
  debugLog('📞 Extracted contact info', contactInfo);
  
  // Step 5: Apply resume-specific cleaning
  cleanedText = cleanResumeText(cleanedText);
  
  const cleanedLength = cleanedText.length;
  const compressionRatio = originalLength > 0 ? (originalLength - cleanedLength) / originalLength : 0;
  
  const metadata = {
    hadPDFArtifacts: rawText.includes('obj') || rawText.includes('endobj'),
    hadAdobeMetadata: rawText.toLowerCase().includes('adobe'),
    hadMicrosoftMetadata: rawText.toLowerCase().includes('microsoft word') || rawText.includes("D:+00'00'"),
    compressionRatio: Math.round(compressionRatio * 100) / 100
  };
  
  debugLog('🎉 ENHANCED text cleaning completed', {
    originalLength,
    cleanedLength,
    compressionRatio: `${Math.round(compressionRatio * 100)}%`,
    metadata
  });
  
  return {
    cleanedText,
    originalLength,
    cleanedLength,
    contactInfo,
    metadata
  };
}

/**
 * Resume-specific text cleaning (ENHANCED)
 */
function cleanResumeText(text: string): string {
  return text
    // Remove Microsoft Word metadata
    .replace(/User\s+Microsoft\s+Word\s+D:\+00'00'/gi, '')
    .replace(/Microsoft\s+Word\s+D:\+00'00'/gi, '')
    .replace(/D:\+00'00'/gi, '')
    
    // Clean up common resume sections
    .replace(/\bPROFESSIONAL\s+SUMMARY\b/gi, '\n\nPROFESSIONAL SUMMARY\n')
    .replace(/\bWORK\s+EXPERIENCE\b/gi, '\n\nWORK EXPERIENCE\n')
    .replace(/\bEXPERIENCE\b/gi, '\n\nEXPERIENCE\n')
    .replace(/\bEDUCATION\b/gi, '\n\nEDUCATION\n')
    .replace(/\bSKILLS\b/gi, '\n\nSKILLS\n')
    .replace(/\bCONTACT\b/gi, '\n\nCONTACT\n')
    
    // Format dates
    .replace(/\b(\d{4})\s*-\s*(\d{4}|\bPresent\b)\b/g, '$1 - $2')
    .replace(/\b(\w+)\s+(\d{4})\s*-\s*(\w+)\s+(\d{4})\b/g, '$1 $2 - $3 $4')
    
    .trim();
}

/**
 * Quick text preview for UI display
 */
export function getCleanTextPreview(text: string, maxLength: number = 300): string {
  if (!text) return '';
  
  const cleaned = cleanExtractedText(text).cleanedText;
  
  if (cleaned.length <= maxLength) {
    return cleaned;
  }
  
  // Try to break at a sentence or word boundary
  const truncated = cleaned.substring(0, maxLength);
  const lastSentence = truncated.lastIndexOf('.');
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSentence > maxLength * 0.7) {
    return truncated.substring(0, lastSentence + 1);
  } else if (lastSpace > maxLength * 0.8) {
    return truncated.substring(0, lastSpace) + '...';
  } else {
    return truncated + '...';
  }
}

/**
 * Validate if extracted text looks reasonable
 */
export function validateExtractedText(text: string): {
  isValid: boolean;
  issues: string[];
  score: number;
} {
  const issues: string[] = [];
  let score = 100;
  
  if (!text || text.length < 50) {
    issues.push('Text too short');
    score -= 50;
  }
  
  // Check for excessive PDF artifacts
  const pdfArtifactRatio = (text.match(/\b(obj|endobj|stream|endstream)\b/gi) || []).length / (text.split(' ').length || 1);
  if (pdfArtifactRatio > 0.1) {
    issues.push('Contains too many PDF artifacts');
    score -= 30;
  }
  
  // Check for Microsoft Word metadata
  if (text.includes("Microsoft Word") || text.includes("D:+00'00'")) {
    issues.push('Contains Microsoft Word metadata');
    score -= 20;
  }
  
  // Check for Adobe metadata
  if (text.toLowerCase().includes('adobe') || text.toLowerCase().includes('identity')) {
    issues.push('Contains Adobe metadata');
    score -= 20;
  }
  
  // Check for readable content
  const readableWords = text.match(/\b[a-zA-Z]{3,}\b/g) || [];
  if (readableWords.length < 10) {
    issues.push('Insufficient readable content');
    score -= 40;
  }
  
  // Check for excessive special characters
  const specialCharRatio = (text.match(/[^a-zA-Z0-9\s]/g) || []).length / text.length;
  if (specialCharRatio > 0.3) {
    issues.push('Too many special characters');
    score -= 20;
  }
  
  return {
    isValid: score >= 60,
    issues,
    score: Math.max(0, score)
  };
}